import axios from 'axios'

class AppCache {
  constructor() {
    this.modelCache = {}
    this.configCache = {}
    this.CACHE_VERSION = 'v2_'
    this.STORAGE_PREFIX = `app_cache_${this.CACHE_VERSION}`
    this.CACHE_DURATION = 4 * 60 * 60 * 1000
  }

  getStorageKey(type, key) {
    return `${this.STORAGE_PREFIX}${type}_${key}`
  }

  getFromStorage(storageKey) {
    try {
      const item = sessionStorage.getItem(storageKey)
      if (!item) return null

      const parsed = JSON.parse(item)
      if (Date.now() - parsed.timestamp > this.CACHE_DURATION) {
        sessionStorage.removeItem(storageKey)
        return null
      }
      return parsed.data
    } catch (e) {
      return null
    }
  }

  saveToStorage(storageKey, data) {
    try {
      const item = {
        timestamp: Date.now(),
        data: data
      }
      sessionStorage.setItem(storageKey, JSON.stringify(item))
    } catch (e) {
      console.error('保存缓存失败:', e)
    }
  }

  async getModelsByCategory(category, freeOnly = false, capability = null) {
    const cacheKey = capability ? `${category}_${capability}` : category
    const storageKey = this.getStorageKey('models', cacheKey)
    let models = this.getFromStorage(storageKey)

    if (models) {
      console.log(`[AppCache] 从缓存加载${category}模型(${capability || 'all'}):`, models)
      this.modelCache[cacheKey] = models
      return models
    }

    try {
      console.log(`[AppCache] 正在从API获取${category}模型(${capability || 'all'})...`)
      const response = await axios.get('/api/admin/models')
      console.log(`[AppCache] API响应:`, response.data)
      if (response.data.code === 200) {
        const allModels = response.data.data
        console.log(`[AppCache] 所有模型数量:`, allModels.length)
        models = allModels.filter(m => m.category === category && m.status)
        
        // 按能力过滤
        if (capability) {
          const capabilityMap = {
            'text_to_image': 'text_to_image',
            'image_to_image': 'image_to_image',
            'text_to_video': 'text_to_video',
            'image_to_video': 'image_to_video'
          }
          const targetCapability = capabilityMap[capability] || capability
          models = models.filter(m => {
            const caps = m.capabilities || []
            return caps.includes(targetCapability)
          })
          console.log(`[AppCache] 按能力(${targetCapability})筛选后模型数量:`, models.length)
        }
        
        // 按免费过滤
        if (freeOnly) {
          models = models.filter(m => m.model_type === 'free')
        }
        
        console.log(`[AppCache] 最终${category}模型(${capability || 'all'})数量:`, models.length)
        console.log(`[AppCache] ${category}模型详情:`, models)
        this.modelCache[cacheKey] = models
        this.saveToStorage(storageKey, models)
        return models
      } else {
        console.error(`[AppCache] API返回错误码:`, response.data.code)
      }
    } catch (error) {
      console.error(`[AppCache] 获取${category}模型失败:`, error.response || error)
    }
    return []
  }

  async getTextConfig() {
    const storageKey = this.getStorageKey('config', 'text')
    let config = this.getFromStorage(storageKey)

    if (config) {
      this.configCache.text = config
      return config
    }

    try {
      const response = await axios.get('/api/text-config')
      console.log('[AppCache] text-config API响应:', response.data)
      if (response.data.code === 200) {
        config = response.data.data
        console.log('[AppCache] text-config解析后:', config)
        this.configCache.text = config
        this.saveToStorage(storageKey, config)
        return config
      }
    } catch (error) {
      console.error('获取文案配置失败:', error)
    }
    return null
  }

  async getVideoSizes() {
    const storageKey = this.getStorageKey('config', 'video_sizes')
    let sizes = this.getFromStorage(storageKey)

    if (sizes) {
      return sizes
    }

    try {
      const response = await axios.get('/api/video-sizes')
      if (response.data.code === 200) {
        sizes = response.data.video_sizes || response.data.sizes
        this.saveToStorage(storageKey, sizes)
        return sizes
      }
    } catch (error) {
      console.error('获取视频尺寸失败:', error)
    }
    return []
  }

  async getAspectRatios() {
    const storageKey = this.getStorageKey('config', 'aspect_ratios')
    let ratios = this.getFromStorage(storageKey)

    if (ratios) {
      return ratios
    }

    try {
      const response = await axios.get('/api/aspect-ratios')
      if (response.data.code === 200) {
        ratios = response.data.ratios
        this.saveToStorage(storageKey, ratios)
        return ratios
      }
    } catch (error) {
      console.error('获取比例配置失败:', error)
    }
    return []
  }

  async getImageSizes() {
    const storageKey = this.getStorageKey('config', 'image_sizes')
    let sizes = this.getFromStorage(storageKey)

    if (sizes) {
      return sizes
    }

    try {
      const response = await axios.get('/api/image-sizes')
      if (response.data.code === 200) {
        sizes = response.data.image_sizes || response.data.sizes
        this.saveToStorage(storageKey, sizes)
        return sizes
      }
    } catch (error) {
      console.error('获取图片尺寸失败:', error)
    }
    return []
  }

  async preloadAll() {
    try {
      await Promise.all([
        this.getModelsByCategory('image'),
        this.getModelsByCategory('video'),
        this.getModelsByCategory('text'),
        this.getModelsByCategory('drama'),
        this.getTextConfig(),
        this.getVideoSizes(),
        this.getAspectRatios(),
        this.getImageSizes()
      ])
    } catch (error) {
      console.error('预加载缓存失败:', error)
    }
  }

  refreshCache() {
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    })
    this.modelCache = {}
    this.configCache = {}
  }

  refreshModelCache() {
    const categories = ['image', 'video', 'text', 'drama']
    categories.forEach(category => {
      const storageKey = this.getStorageKey('models', category)
      sessionStorage.removeItem(storageKey)
      if (this.modelCache[category]) {
        delete this.modelCache[category]
      }
    })
  }

  refreshConfigCache() {
    const configKeys = ['text', 'video_sizes', 'aspect_ratios', 'image_sizes']
    configKeys.forEach(key => {
      const storageKey = this.getStorageKey('config', key)
      sessionStorage.removeItem(storageKey)
    })
  }
}

const appCache = new AppCache()
export default appCache
